import React, { useState } from 'react';
import { apiUrl } from '../api';
import { Job, User, WorkerCommitment, JobStatus, DroneMission, JobType, Crew } from '../types';

interface Props {
  jobs: Job[];
  missions: DroneMission[];
  user: User;
  crews: Crew[];
  onUpdateJob: (job: Job) => void;
}

/**
 * Calendar day key in LOCAL time.
 *
 * `new Date("YYYY-MM-DD")` is parsed as UTC midnight, which for anyone west of UTC lands on the
 * previous day: a job quick-added "today" would drop into yesterday's cell. Comparing on a local
 * calendar key avoids that. Full ISO timestamps still convert normally.
 */
const localDayKey = (value?: string | Date | null): string => {
  if (!value) return '';
  const d = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00`)
    : new Date(value as any);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const ROLES = ['Climber', 'Groundsman', 'Foreman', 'Equipment Operator', 'Arborist'];
const EQUIPMENT_OPTIONS = ['Chainsaw', 'Wood Chipper', 'Bucket Truck', 'Stump Grinder', 'Rigging Gear', 'Crane', 'Skid Steer'];

const CalendarView = ({ jobs, missions, onSelectJob, selectedDate }: { jobs: Job[], missions: DroneMission[], onSelectJob: (id: string) => void, selectedDate: Date | null }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  
  const startDate = new Date(startOfMonth);
  startDate.setDate(startDate.getDate() - startDate.getDay()); 
  
  const endDate = new Date(endOfMonth);
  if (endDate.getDay() !== 6) {
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));
  }
  
  const days = [];
  let day = new Date(startDate);
  while (day <= endDate) {
    days.push(new Date(day));
    day.setDate(day.getDate() + 1);
  }
  
  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  
  return (
    <div className="flex flex-col h-full bg-white rounded-2xl border-2 border-stone-200 shadow-sm p-6 overflow-hidden">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-black text-bark">
          {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
        </h3>
        <div className="flex space-x-2">
          <button onClick={prevMonth} className="p-2 hover:bg-stone-100 rounded-lg text-stone-500 hover:text-bark transition">&lt;</button>
          <button onClick={() => setCurrentDate(new Date())} className="px-4 py-2 hover:bg-stone-100 rounded-lg font-bold text-sm text-stone-500 hover:text-bark transition">Today</button>
          <button onClick={nextMonth} className="p-2 hover:bg-stone-100 rounded-lg text-stone-500 hover:text-bark transition">&gt;</button>
        </div>
      </div>
      
      <div className="grid grid-cols-7 gap-px bg-stone-200 rounded-lg overflow-hidden flex-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="bg-stone-50 p-2 text-center text-xs font-black text-stone-400 uppercase tracking-widest">
            {d}
          </div>
        ))}
        {days.map((d, i) => {
          const isCurrentMonth = d.getMonth() === currentDate.getMonth();
          const isToday = d.toDateString() === new Date().toDateString();
          const isSelected = selectedDate && d.toDateString() === selectedDate.toDateString();
          const dayJobs = jobs.filter(j => j.scheduledDate && localDayKey(j.scheduledDate) === localDayKey(d));
          const dayMissions = missions.filter(m => m.scheduledDate && localDayKey(m.scheduledDate) === localDayKey(d));
          
          return (
            <div 
              key={i} 
              className={`bg-white p-2 min-h-[100px] flex flex-col transition ${!isCurrentMonth ? 'opacity-50' : ''} ${isSelected ? 'ring-2 ring-inset ring-palm bg-palm/5' : ''}`}
            >
              <div className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full mb-1 ${isToday ? 'bg-gecko text-white' : 'text-stone-700'}`}>
                {d.getDate()}
              </div>
              <div className="space-y-1 overflow-y-auto flex-1 custom-scrollbar">
                {dayJobs.map(j => (
                  <div 
                    key={j.id} 
                    onClick={(e) => { e.stopPropagation(); onSelectJob(j.id); }}
                    className="text-[10px] p-1.5 rounded bg-leaf/20 text-palm font-bold truncate cursor-pointer hover:bg-leaf/40 transition"
                  >
                    {new Date(j.scheduledDate!).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - {j.clientName}
                  </div>
                ))}
                {dayMissions.map(m => (
                  <div 
                    key={m.id} 
                    onClick={(e) => { e.stopPropagation(); onSelectJob(m.id); }}
                    className="text-[10px] p-1.5 rounded bg-sky-100 text-sky-700 font-bold truncate cursor-pointer hover:bg-sky-200 transition"
                  >
                    🚁 {new Date(m.scheduledDate!).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - {m.missionType}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ScheduleView: React.FC<Props> = ({ jobs, missions, user, crews, onUpdateJob }) => {
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [role, setRole] = useState(ROLES[0]);
  const [selectedEquipment, setSelectedEquipment] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const [dispatchingJob, setDispatchingJob] = useState<Job | null>(null);
  const [reschedulingJobId, setReschedulingJobId] = useState<string | null>(null);

  /**
   * One-tap reschedule: move a job to today / tomorrow / next week at 08:00, or to a picked
   * time. Writes a LOCAL datetime string, which the calendar day-key matching handles correctly.
   */
  const applyReschedule = (job: Job, daysFromToday: number) => {
    const d = new Date();
    d.setDate(d.getDate() + daysFromToday);
    d.setHours(8, 0, 0, 0);
    const pad = (n: number) => String(n).padStart(2, '0');
    onUpdateJob({
      ...job,
      scheduledDate: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`,
    });
    setReschedulingJobId(null);
  };

  // Anything with a date that is still active work belongs on the day's schedule - including
  // estimate visits, which are jobs in ESTIMATING. Previously only SCHEDULED and IN_PROGRESS were
  // included, so a booked estimate never appeared on the schedule at all and the day looked empty.
  const allScheduled = [
    ...jobs.filter(j => j.status === JobStatus.ESTIMATING || j.status === JobStatus.SCHEDULED || j.status === JobStatus.IN_PROGRESS)
      .map(j => ({ ...j, type: JobType.TREE_WORK })),
    ...missions.filter(m => m.status === 'Planned' || m.status === 'In Progress')
      .map(m => {
        const job = jobs.find(j => j.id === m.jobId);
        return {
          id: m.id,
          clientName: job?.clientName || 'Drone Mission',
          address: job?.address || 'Remote Site',
          status: m.status as unknown as JobStatus,
          scheduledDate: m.scheduledDate,
          type: JobType.DRONE_MISSION,
          missionDetails: m,
          inventory: [],
          lineItems: [],
          totalPrice: m.price || 0,
          safetyChecked: false,
          documents: []
        } as Job;
      })
  ].sort((a, b) => new Date(a.scheduledDate || '').getTime() - new Date(b.scheduledDate || '').getTime());

  const displayedJobs = selectedDate 
    ? allScheduled.filter(j => j.scheduledDate && localDayKey(j.scheduledDate) === localDayKey(selectedDate))
    : allScheduled;

  const handleCommit = () => {
    if (!selectedJob) return;
    const newCommitment: WorkerCommitment = {
      workerId: user.email,
      workerName: user.name,
      role,
      equipment: selectedEquipment
    };
    const existingCommitments = selectedJob.commitments || [];
    const updatedCommitments = existingCommitments.filter(c => c.workerId !== user.email);
    updatedCommitments.push(newCommitment);

    onUpdateJob({
      ...selectedJob,
      commitments: updatedCommitments
    });
    setSelectedJob(null);
    setRole(ROLES[0]);
    setSelectedEquipment([]);
  };

  const toggleEquipment = (eq: string) => {
    setSelectedEquipment(prev => 
      prev.includes(eq) ? prev.filter(e => e !== eq) : [...prev, eq]
    );
  };

  const removeCommitment = (job: Job) => {
    const existingCommitments = job.commitments || [];
    const updatedCommitments = existingCommitments.filter(c => c.workerId !== user.email);
    onUpdateJob({
      ...job,
      commitments: updatedCommitments
    });
  };

  const updateDispatchStatus = (job: Job, status: 'PENDING' | 'EN_ROUTE' | 'ON_SITE' | 'COMPLETED') => {
    onUpdateJob({
      ...job,
      dispatchStatus: status
    });
  };

  const assignCrew = (job: Job, crewId: string) => {
    onUpdateJob({
      ...job,
      crewId
    });
    setDispatchingJob(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-black text-bark tracking-tight">Schedule & Dispatch</h2>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 flex flex-col space-y-6">
          <div className="h-[400px]">
            <CalendarView 
              jobs={allScheduled.filter(j => j.type === JobType.TREE_WORK)} 
              missions={missions}
              onSelectJob={(id) => {
                const job = allScheduled.find(j => j.id === id);
                if (job && job.scheduledDate) {
                  setSelectedDate(new Date(job.scheduledDate));
                }
              }} 
              selectedDate={selectedDate} 
            />
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-black text-bark">
                {selectedDate ? `Jobs for ${selectedDate.toLocaleDateString()}` : 'All Scheduled Jobs'}
              </h3>
              {selectedDate && (
                <button onClick={() => setSelectedDate(null)} className="text-sm font-bold text-stone-500 hover:text-bark">
                  Clear Filter
                </button>
              )}
            </div>
            {displayedJobs.length === 0 ? (
              <div className="bg-white p-12 rounded-2xl border-2 border-stone-200 text-center">
                <p className="text-stone-400 font-bold text-lg">No jobs scheduled.</p>
              </div>
            ) : (
              displayedJobs.map(job => {
              const myCommitment = job.commitments?.find(c => c.workerId === user.email);
              const dateStr = job.scheduledDate ? new Date(job.scheduledDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'Unscheduled';
              const assignedCrew = crews.find(c => c.id === job.crewId);

              return (
                <div key={job.id} className="bg-white rounded-2xl border-2 border-stone-200 p-6 shadow-sm">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center space-x-3 mb-1">
                        <span className="px-3 py-1 bg-palm/10 text-palm rounded-full text-xs font-black uppercase tracking-widest">
                          {dateStr}
                        </span>
                        <span className="px-3 py-1 bg-stone-100 text-stone-600 rounded-full text-xs font-black uppercase tracking-widest">
                          {job.status}
                        </span>
                        {job.dispatchStatus && (
                          <span className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest ${
                            job.dispatchStatus === 'EN_ROUTE' ? 'bg-sky-100 text-sky-700' :
                            job.dispatchStatus === 'ON_SITE' ? 'bg-gecko/20 text-gecko' :
                            job.dispatchStatus === 'COMPLETED' ? 'bg-stone-200 text-stone-600' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {job.dispatchStatus.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl font-black text-bark">{job.clientName}</h3>
                      <p className="text-stone-500 font-medium">{job.address}</p>
                      
                      {assignedCrew && (
                        <p className="mt-2 text-sm font-bold text-palm">Assigned to: {assignedCrew.name}</p>
                      )}
                    </div>
                    
                    <div className="flex space-x-2">
                        <button 
                          onClick={async () => {
                            const response = await fetch(apiUrl('/api/ai/suggest-schedule'), {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ jobId: job.id })
                            });
                            const suggestion = await response.json();
                            alert(`Suggested Crew: ${suggestion.suggestedCrew.join(', ')}\nReasoning: ${suggestion.reasoning}`);
                          }}
                          className="px-4 py-2 bg-leaf text-bark rounded-xl font-bold text-sm hover:bg-gecko hover:text-white transition"
                        >
                          Suggest Crew
                        </button>
                      <button 
                        onClick={() => setDispatchingJob(job)}
                        className="px-4 py-2 bg-stone-100 text-stone-700 rounded-xl font-bold text-sm hover:bg-stone-200 transition"
                      >
                        Dispatch / Re-assign
                      </button>
                      <button 
                        onClick={() => setReschedulingJobId(reschedulingJobId === job.id ? null : job.id)}
                        className="px-4 py-2 bg-stone-100 text-stone-700 rounded-xl font-bold text-sm hover:bg-stone-200 transition"
                      >
                        Reschedule
                      </button>
                      {myCommitment ? (
                        <button 
                          onClick={() => removeCommitment(job)}
                          className="px-4 py-2 bg-red-50 text-red-600 rounded-xl font-bold text-sm hover:bg-red-100 transition"
                        >
                          Withdraw
                        </button>
                      ) : (
                        <button 
                          onClick={() => setSelectedJob(job)}
                          className="px-4 py-2 bg-palm text-white rounded-xl font-bold text-sm hover:bg-bark transition shadow-lg shadow-palm/20"
                        >
                          Commit to Job
                        </button>
                      )}
                    </div>
                  </div>

                  {reschedulingJobId === job.id && (
                    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <p className="text-[10px] font-black uppercase tracking-widest text-amber-700 mb-2">Move this job to</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <button onClick={() => applyReschedule(job, 0)} className="px-3 py-1.5 bg-gecko text-white rounded-lg text-xs font-bold hover:bg-palm transition">Today</button>
                        <button onClick={() => applyReschedule(job, 1)} className="px-3 py-1.5 bg-white border border-stone-200 rounded-lg text-xs font-bold text-bark hover:bg-stone-50 transition">Tomorrow</button>
                        <button onClick={() => applyReschedule(job, 7)} className="px-3 py-1.5 bg-white border border-stone-200 rounded-lg text-xs font-bold text-bark hover:bg-stone-50 transition">Next week</button>
                        <span className="text-xs font-bold text-stone-400 px-1">or pick a time</span>
                        <input
                          type="datetime-local"
                          onChange={(e) => {
                            if (e.target.value) {
                              onUpdateJob({ ...job, scheduledDate: e.target.value });
                              setReschedulingJobId(null);
                            }
                          }}
                          className="px-3 py-1.5 bg-white border border-stone-200 rounded-lg text-xs font-bold text-bark"
                        />
                        <button onClick={() => setReschedulingJobId(null)} className="px-3 py-1.5 text-stone-500 text-xs font-bold hover:text-bark transition">Cancel</button>
                      </div>
                    </div>
                  )}

                  <div className="border-t border-stone-100 pt-4 mt-4 grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-xs font-black text-stone-400 uppercase tracking-widest mb-3">Crew Commitments</h4>
                      {(!job.commitments || job.commitments.length === 0) ? (
                        <p className="text-sm text-stone-400 italic">No crew committed yet.</p>
                      ) : (
                        <div className="space-y-3">
                          {job.commitments.map((c, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-stone-50 p-2 rounded-xl">
                              <div className="flex items-center space-x-3">
                                <div className="w-6 h-6 rounded-full bg-palm text-white flex items-center justify-center font-bold text-xs">
                                  {(c.workerName?.charAt?.(0) ?? '?')}
                                </div>
                                <div>
                                  <p className="font-bold text-bark text-xs">{c.workerName} <span className="text-[10px] text-stone-400 font-normal">({c.role})</span></p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div>
                       <h4 className="text-xs font-black text-stone-400 uppercase tracking-widest mb-3">Dispatch Status</h4>
                       <div className="flex flex-wrap gap-2">
                          {['PENDING', 'EN_ROUTE', 'ON_SITE', 'COMPLETED'].map(status => (
                            <button
                              key={status}
                              onClick={() => updateDispatchStatus(job, status as any)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                                job.dispatchStatus === status 
                                ? 'bg-gecko text-white shadow' 
                                : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                              }`}
                            >
                              {status.replace('_', ' ')}
                            </button>
                          ))}
                       </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white p-6 rounded-2xl border-2 border-stone-200 shadow-sm">
            <h3 className="text-lg font-black text-bark mb-4">Route Optimization (Map)</h3>
            <div className="bg-stone-100 rounded-xl h-64 flex items-center justify-center">
               <p className="text-stone-400 font-bold text-sm">Interactive map would load here</p>
            </div>
            <button className="w-full mt-4 py-3 bg-stone-100 text-bark rounded-xl font-bold hover:bg-stone-200 transition">
              Optimize Today's Routes
            </button>
          </div>
          
          <div className="bg-white p-6 rounded-2xl border-2 border-stone-200 shadow-sm">
            <h3 className="text-lg font-black text-bark mb-4">My Schedule</h3>
            <div className="space-y-4">
              {jobs.filter(j => j.commitments?.some(c => (c as any).workerId === user?.email)).length === 0 ? (
                <p className="text-sm text-stone-400 italic">You haven't committed to any upcoming jobs.</p>
              ) : (
                jobs.filter(j => j.commitments?.some(c => (c as any).workerId === user?.email)).map(job => (
                  <div key={job.id} className="p-3 bg-palm/5 rounded-xl border border-palm/20">
                    <p className="font-bold text-bark text-sm">{job.clientName}</p>
                    <p className="text-xs text-stone-500">{job.scheduledDate ? new Date(job.scheduledDate).toLocaleDateString() : 'Unscheduled'}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedJob && (
        <div className="fixed inset-0 bg-bark/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <h3 className="text-2xl font-black text-bark mb-2">Commit to Job</h3>
            <p className="text-stone-500 mb-6">{selectedJob.clientName} - {selectedJob.scheduledDate ? new Date(selectedJob.scheduledDate).toLocaleDateString() : 'Unscheduled'}</p>

            <div className="space-y-6">
              <div>
                <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Your Role</label>
                <select 
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full p-3 bg-stone-50 border-2 border-stone-200 rounded-xl font-bold text-bark focus:border-palm focus:outline-none"
                >
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Equipment Bringing</label>
                <div className="flex flex-wrap gap-2">
                  {EQUIPMENT_OPTIONS.map(eq => (
                    <button
                      key={eq}
                      onClick={() => toggleEquipment(eq)}
                      className={`px-3 py-2 rounded-xl text-sm font-bold border-2 transition ${
                        selectedEquipment.includes(eq)
                          ? 'bg-palm border-palm text-white'
                          : 'bg-white border-stone-200 text-stone-600 hover:border-stone-300'
                      }`}
                    >
                      {eq}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setSelectedJob(null)}
                  className="flex-1 py-3 bg-stone-100 text-stone-600 rounded-xl font-bold hover:bg-stone-200 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCommit}
                  className="flex-1 py-3 bg-palm text-white rounded-xl font-bold hover:bg-bark transition shadow-lg shadow-palm/20"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {dispatchingJob && (
        <div className="fixed inset-0 bg-bark/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <h3 className="text-2xl font-black text-bark mb-2">Dispatch Crew</h3>
            <p className="text-stone-500 mb-6">{dispatchingJob.clientName}</p>

            <div className="space-y-4">
              {crews.map(crew => (
                <button
                  key={crew.id}
                  onClick={() => assignCrew(dispatchingJob, crew.id)}
                  className="w-full p-4 text-left border-2 border-stone-200 rounded-xl hover:border-palm transition"
                >
                  <p className="font-bold text-bark">{crew.name}</p>
                  <p className="text-xs text-stone-500">{crew.members.join(', ')}</p>
                </button>
              ))}
              
              <button
                  onClick={() => setDispatchingJob(null)}
                  className="w-full mt-4 py-3 bg-stone-100 text-stone-600 rounded-xl font-bold hover:bg-stone-200 transition"
                >
                  Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleView;
